import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Typography,
  CircularProgress,
} from '@mui/material'
import { Edit } from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'

const EditWeightDialog = ({
  open,
  onClose,
  onSave,
  entry,
  weightIndex,
  isSubmitting = false
}) => {
  const [weight, setWeight] = useState('')
  const [unit, setUnit] = useState('lbs')
  const [date, setDate] = useState('')

  // Populate form when dialog opens
  useEffect(() => {
    if (open && entry?.weights && weightIndex !== null && entry.weights[weightIndex]) {
      const weightEntry = entry.weights[weightIndex]
      
      // Use original weight and unit if available, otherwise convert from grams
      if (weightEntry.originalWeight && weightEntry.originalUnit) {
        setWeight(weightEntry.originalWeight.toString())
        setUnit(weightEntry.originalUnit)
      } else {
        // Convert from grams to lbs as default
        const weightInLbs = (weightEntry.weightGrams / 453.592).toFixed(2)
        setWeight(weightInLbs)
        setUnit('lbs')
      }
      
      setDate(weightEntry.date)
    }
  }, [open, entry, weightIndex])

  const handleSave = () => {
    if (!weight || !date) return
    
    onSave({
      weight: parseFloat(weight),
      unit,
      date
    })
  }

  const handleClose = () => {
    setWeight('')
    setUnit('lbs')
    setDate('')
    onClose()
  }

  const currentWeightEntry = entry?.weights?.[weightIndex]

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { margin: { xs: 1, sm: 2 } }
      }}
    >
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <Edit color="primary" />
          <Typography variant="h6">Edit Weight Entry</Typography>
        </Stack>
      </DialogTitle>
      
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {currentWeightEntry && (
            <Typography variant="body2" color="text.secondary">
              Editing weight entry from {dayjs(currentWeightEntry.date).format('MMMM D, YYYY')}
              {currentWeightEntry.recordedBy && ` • Recorded by ${currentWeightEntry.recordedBy}`}
            </Typography>
          )}

          <TextField
            label="Weight"
            type="number"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            inputProps={{ 
              step: unit === 'g' ? '1' : '0.01',
              min: '0'
            }}
            required
            fullWidth
          />

          <FormControl fullWidth required>
            <InputLabel>Unit</InputLabel>
            <Select
              value={unit}
              label="Unit"
              onChange={(e) => setUnit(e.target.value)}
            >
              <MenuItem value="lbs">Pounds (lbs)</MenuItem>
              <MenuItem value="oz">Ounces (oz)</MenuItem>
              <MenuItem value="g">Grams (g)</MenuItem>
            </Select>
          </FormControl>

          <DatePicker
            label="Date"
            value={date ? dayjs(date) : null}
            onChange={(newValue) => setDate(newValue ? newValue.format('YYYY-MM-DD') : '')}
            maxDate={dayjs()}
            slotProps={{
              textField: {
                required: true,
                fullWidth: true
              }
            }}
          />
        </Stack>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isSubmitting || !weight || !date}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : <Edit />}
        >
          {isSubmitting ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default EditWeightDialog